// New homepage (video-hero layout).
//
// Replaces the previous 3D/cinematic homepage, which is preserved intact at
// /experience (see ExperiencePage.jsx). Deliberately non-3D: a full-screen
// video hero followed by conventional, well-spaced marketing sections. The
// one piece carried over from the old home is the ConstructionPhase
// storyboard, which the client liked.
//
// Content comes from `homeV2` in the siteContent store, so everything here
// is editable from /admin -> Content -> Home (New).

import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "../components/site/PageShell";
import Reveal from "../components/site/Reveal";
import SplitReveal from "../components/site/SplitReveal";
import gsap from "gsap";
import { openLeadModal } from "../components/LeadModal";
import { useGsapScene } from "../components/site/useGsapScene";
import { useContent } from "../state/siteContent";
import { getProjects, statusLabel } from "../state/projectsStore";
import { projectImage } from "../state/placeholders";
import "./site.css";
import "./home-v2.css";

const ConstructionPhase = lazy(() => import("../components/ConstructionPhase"));

/* ─────────────────────────── Hero ─────────────────────────── */

function Hero({ c }) {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  // (Hero copy/CTAs removed — the video carries its own messaging, so the
  // magnetic-button refs are no longer needed.)
  // YouTube shows a large play-button / branding overlay during the first
  // ~1s of autoplay startup that `controls=0` can't suppress. We keep the
  // iframe hidden (poster covers it) and fade it in only after that window,
  // so the startup UI is never visible.
  const [ytReady, setYtReady] = useState(false);

  // Autoplay is unreliable on mobile (data saver, low-power mode). The poster
  // image is always painted underneath, so a blocked video degrades to a
  // still hero rather than a black screen.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !c.heroVideo) return;
    const play = v.play();
    if (play && typeof play.catch === "function") play.catch(() => {});
  }, [c.heroVideo]);

  // Reveal the YouTube background past its startup overlay.
  useEffect(() => {
    if (!c.heroYouTubeId) return undefined;
    const t = setTimeout(() => setYtReady(true), 1400);
    return () => clearTimeout(t);
  }, [c.heroYouTubeId]);

  // CINEMATIC HERO → ABOUT HANDOFF.
  // As the user scrolls the first viewport, the hero pins briefly while the
  // media zooms and the copy parallaxes up faster, then the whole hero
  // recedes (scale down + radius + darken) so the next section reads as
  // taking over the same "camera", not a hard cut.
  const sceneRef = useGsapScene((self, q, root) => {
    const media = q(".hv-hero__media")[0];
    const inner = q(".hv-hero__inner")[0];
    const scrim = q(".hv-hero__scrim")[0];
    const cue = q(".hv-hero__cue")[0];
    if (!media) return;

    // Phase 1 — scrub through the hero itself (media zoom + copy parallax).
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: "top top",
        end: "+=90%",
        scrub: 0.6,
      },
    });
    tl.to(media, { scale: 1.12, ease: "none" }, 0);
    if (inner) tl.to(inner, { yPercent: -22, ease: "none" }, 0);
    tl.to(cue, { autoAlpha: 0, ease: "none" }, 0)
      .to(scrim, { opacity: 1.15, ease: "none" }, 0);

    // Phase 2 — the hero container recedes as its bottom edge leaves, so the
    // About section below appears to rise over a still-visible, shrinking hero.
    gsap.fromTo(
      root,
      { scale: 1, borderRadius: 0 },
      {
        scale: 0.92,
        borderRadius: 28,
        ease: "none",
        scrollTrigger: {
          trigger: root,
          start: "bottom bottom",
          end: "bottom top",
          scrub: 0.6,
        },
      }
    );
  });

  return (
    <section className="hv-hero" ref={sceneRef}>
      <div className="hv-hero__media">
        {c.heroPoster && (
          <img
            src={c.heroPoster}
            alt=""
            aria-hidden="true"
            className="hv-hero__poster"
            fetchpriority="high"
          />
        )}
        {c.heroYouTubeId ? (
          /* YouTube background: muted, autoplaying, looping, no controls/UI.
             The iframe is scaled to cover the viewport (iframes can't
             object-fit) via the .hv-hero__yt wrapper. `playlist=<id>` is
             required for `loop` to work on a single video. */
          <div className={`hv-hero__yt ${ytReady ? "is-ready" : ""}`} aria-hidden="true">
            <iframe
              className="hv-hero__yt-frame"
              src={`https://www.youtube-nocookie.com/embed/${c.heroYouTubeId}?autoplay=1&mute=1&loop=1&playlist=${c.heroYouTubeId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&fs=0&iv_load_policy=3`}
              title="Nausiddhi Realty"
              allow="autoplay; encrypted-media"
              frameBorder="0"
            />
          </div>
        ) : (
          c.heroVideo && (
            <video
              ref={videoRef}
              className={`hv-hero__video ${videoReady ? "is-ready" : ""}`}
              src={c.heroVideo}
              poster={c.heroPoster || undefined}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden="true"
              onCanPlay={() => setVideoReady(true)}
            />
          )
        )}
        <div className="hv-hero__scrim" aria-hidden="true" />
      </div>

      {/* Hero headline/sub stay off the video (it carries its own visual
          message) but two action buttons anchor the first screen so a
          first-time visitor always has an immediate, obvious next step —
          browse the portfolio, or go straight to booking a visit. */}
      <div className="hv-hero__actions">
        <Link to="/projects" className="hv-btn hv-btn--primary">
          {c.heroPrimaryCta}
        </Link>
        <button type="button" className="hv-btn hv-btn--ghost-light" onClick={openLeadModal}>
          {c.heroSecondaryCta}
        </button>
      </div>

      <div className="hv-hero__cue" aria-hidden="true">
        <span>{c.heroScrollCue}</span>
        <span className="hv-hero__cue-line" />
      </div>
    </section>
  );
}

/* ─────────────────────────── About ─────────────────────────── */

function About({ c }) {
  // CINEMATIC ABOUT — the media reveals via a clip-path wipe, then the
  // container and the image inside it move at DIFFERENT rates as the
  // section passes (container drifts up, image counter-drifts down inside
  // its overflow:hidden frame) for a lens/depth feeling. Heading + copy
  // rise on their own scrub so text participates in the camera move.
  const sceneRef = useGsapScene((self, q, root) => {
    const media = q(".hv-about__media")[0];
    const img = q(".hv-parallax-img")[0];
    const body = q(".hv-about__body")[0];

    // Clip-path reveal of the image frame as it enters.
    if (media) {
      gsap.fromTo(
        media,
        { clipPath: "inset(0 0 100% 0)" },
        {
          clipPath: "inset(0 0 0% 0)",
          ease: "power2.out",
          scrollTrigger: { trigger: media, start: "top 85%", end: "top 45%", scrub: 0.5 },
        }
      );
    }
    // Container / image counter-parallax split (depth).
    if (media && img) {
      gsap.fromTo(
        media, { yPercent: 8 }, {
          yPercent: -8, ease: "none",
          scrollTrigger: { trigger: root, start: "top bottom", end: "bottom top", scrub: 0.8 },
        });
      gsap.fromTo(
        img, { yPercent: -8, scale: 1.12 }, {
          yPercent: 8, scale: 1.02, ease: "none",
          scrollTrigger: { trigger: root, start: "top bottom", end: "bottom top", scrub: 0.8 },
        });
    }
    // Body slides in from the right; heading gets an extra scrubbed rise.
    if (body) {
      gsap.fromTo(
        body, { x: 42, autoAlpha: 0 }, {
          x: 0, autoAlpha: 1, ease: "power2.out",
          scrollTrigger: { trigger: body, start: "top 82%", end: "top 55%", scrub: 0.5 },
        });
    }
    // NOTE: the About heading's entrance is owned by <SplitReveal> (masked
    // line reveal). Deliberately no tween on .hv-title here — animating the
    // heading AND its inner lines at once reads as a double-move.
  });

  return (
    <section className="hv-section hv-about" ref={sceneRef}>
      <div className="hv-about__grid">
        <div className="hv-about__media-wrap">
          <span className="hv-about__frame" aria-hidden="true" />
          <figure className="hv-about__media">
            <img
              className="hv-parallax-img"
              src={c.aboutImage || "/img/home-about.png"}
              alt=""
              loading="lazy"
            />
            <span className="hv-about__media-veil" aria-hidden="true" />
            <figcaption className="hv-about__badge">
              <span className="hv-about__badge-k">Our promise</span>
              <span className="hv-about__badge-v">Land you can build a future on</span>
            </figcaption>
          </figure>
        </div>
        <div className="hv-about__body">
          <div className="hv-eyebrow">{c.aboutEyebrow}</div>
          <SplitReveal as="h2" className="hv-title hv-about__title">{c.aboutTitle}</SplitReveal>
          <p className="hv-lede">{c.aboutBody}</p>
          <ul className="hv-about__points">
            {(c.aboutPoints || []).map((p) => (
              <li key={p}>
                <span className="hv-about__tick" aria-hidden="true" />
                <span className="hv-about__point-text">{p}</span>
              </li>
            ))}
          </ul>
          <Link to="/about" className="hv-link hv-about__cta">
            {c.aboutCta}
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Stats ─────────────────────────── */

// Counts up once, the first time the band scrolls into view.
function useCountUp(target, active, duration = 1600) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (typeof window === "undefined") return undefined;
    // Respect reduced-motion: show the final number immediately.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return value;
}

function StatItem({ stat, active, index = 0 }) {
  const value = useCountUp(Number(stat.value) || 0, active);
  return (
    <div className="hv-stat" style={{ transitionDelay: `${index * 110}ms` }}>
      <div className="hv-stat__value">
        {value.toLocaleString("en-IN")}
        <span className="hv-stat__suffix">{stat.suffix}</span>
      </div>
      <div className="hv-stat__label">{stat.label}</div>
    </div>
  );
}

function Stats({ c }) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setActive(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActive(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Cinematic emerald band: heading rises, the 4 stat tiles stagger up +
  // scale from behind ("pushing toward the camera"). Count-up still handled
  // by the IntersectionObserver above.
  const sceneRef = useGsapScene((self, q, root) => {
    // Only the eyebrow is tweened here — the heading's entrance is owned by
    // <SplitReveal>. Fading the whole head would hide the masked lines while
    // they play, and stack two moves on the same text.
    const eyebrow = q(".hv-stats__head .hv-eyebrow")[0];
    const tiles = q(".hv-stat");
    if (eyebrow) {
      gsap.fromTo(eyebrow, { yPercent: 30, autoAlpha: 0 }, {
        yPercent: 0, autoAlpha: 1, ease: "power2.out",
        scrollTrigger: { trigger: root, start: "top 78%", end: "top 45%", scrub: 0.5 },
      });
    }
    if (tiles.length) {
      gsap.fromTo(tiles, { y: 60, scale: 0.86, autoAlpha: 0 }, {
        y: 0, scale: 1, autoAlpha: 1, ease: "power3.out", stagger: 0.12,
        scrollTrigger: { trigger: root, start: "top 72%", end: "top 40%", scrub: 0.6 },
      });
    }
  });

  const setRefs = (node) => { ref.current = node; sceneRef.current = node; };

  return (
    <section className="hv-section hv-stats-wrap hv-band hv-band--emerald" ref={setRefs}>
      <div className="hv-stats__head">
        <div className="hv-eyebrow">{c.statsEyebrow}</div>
        <SplitReveal as="h2" className="hv-title hv-title--center">{c.statsTitle}</SplitReveal>
      </div>
      <div className={`hv-stats ${active ? "is-in" : ""}`}>
        {(c.stats || []).map((s, i) => (
          <StatItem key={s.label} stat={s} active={active} index={i} />
        ))}
      </div>
    </section>
  );
}

/* ───────────────────── Projects overview ───────────────────── */

// One card. No Reveal wrapper — in the horizontal track the cards start
// off-screen, so an IntersectionObserver reveal would leave them stuck
// invisible; the Projects GSAP scene owns their motion instead.
function ProjectCard({ p }) {
  return (
    <article className="hv-pcard">
      {/* Homepage cards go to the /projects listing page. The individual
          card on THAT page is what redirects out to the V7Vista project. */}
      <Link to="/projects" className="hv-pcard__link">
        <div className="hv-pcard__media">
          <img src={projectImage(p)} alt={p.name} loading="lazy" />
          <span className="hv-pcard__status">{statusLabel(p.statusKey)}</span>
        </div>
        <div className="hv-pcard__body">
          <h3 className="hv-pcard__name">{p.name}</h3>
          <div className="hv-pcard__loc">{p.location}</div>
          <dl className="hv-pcard__facts">
            <div><dt>Area</dt><dd>{p.area || "—"}</dd></div>
            <div><dt>Plots</dt><dd>{p.units || "—"}</dd></div>
            <div><dt>Plot size</dt><dd>{p.plotSize || "—"}</dd></div>
          </dl>
          <span className="hv-pcard__cta">
            View details <span aria-hidden="true">→</span>
          </span>
        </div>
      </Link>
    </article>
  );
}

function Projects({ c }) {
  const [projects, setProjects] = useState(() => getProjects());
  useEffect(() => {
    // Projects can be edited in the backpanel; re-read on mount so the
    // homepage never shows a stale list after an admin edit.
    setProjects(getProjects());
  }, []);

  const shown = projects.slice(0, 4);

  // HORIZONTAL SCROLL — desktop only. The section first scrolls in normally;
  // the pin only engages once the card row is CENTERED in the viewport
  // (start: "center center"), so the cards are always fully visible before
  // horizontal panning begins. The card sizing (CSS) guarantees each card
  // fits within the pinned viewport height. Mobile keeps the vertical grid.
  const sceneRef = useGsapScene((self, q, root) => {
    const mm = gsap.matchMedia();

    // Desktop: horizontal pan while pinned.
    mm.add("(min-width: 1024px)", () => {
      const track = q(".hv-projects__track")[0];
      const viewport = q(".hv-projects__viewport")[0];
      if (!track || !viewport) return;
      const distance = () => Math.max(0, track.scrollWidth - viewport.clientWidth);
      if (distance() <= 0) return;

      gsap.to(track, {
        x: () => -distance(),
        ease: "none",
        scrollTrigger: {
          trigger: root,
          start: "center center",   // pin only once fully in view
          end: () => "+=" + distance(),
          scrub: 0.8,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });
    });

    // Mobile / reduced width: simple stagger-in of the stacked cards.
    mm.add("(max-width: 1023px)", () => {
      const cards = q(".hv-pcard");
      if (!cards.length) return;
      gsap.fromTo(cards, { y: 50, autoAlpha: 0 }, {
        y: 0, autoAlpha: 1, ease: "power3.out", stagger: 0.1,
        scrollTrigger: { trigger: root, start: "top 80%", end: "top 45%", scrub: 0.6 },
      });
    });

    return () => mm.revert();
  }, [shown.length]);

  return (
    <section className="hv-section hv-projects hv-band hv-band--white" ref={sceneRef}>
      {/* Not wrapped in <Reveal>: its opacity:0 start would hide the masked
          heading lines while they play. The eyebrow/lede fade in via Reveal
          individually instead. */}
      <div className="hv-projects__head">
        <div>
          <Reveal as="div" className="hv-eyebrow" variant="up">{c.projectsEyebrow}</Reveal>
          <SplitReveal as="h2" className="hv-title">{c.projectsTitle}</SplitReveal>
        </div>
        <Reveal as="p" className="hv-lede" variant="up" delay={120}>{c.projectsLede}</Reveal>
      </div>

      <div className="hv-projects__viewport">
        <div className="hv-projects__track">
          {shown.map((p) => (
            <ProjectCard key={p.id} p={p} />
          ))}
        </div>
      </div>

      <Reveal as="div" className="hv-projects__foot">
        <Link to="/projects" className="hv-btn hv-btn--outline">
          {c.projectsCta}
        </Link>
      </Reveal>
    </section>
  );
}

/* ─────────────────────── Testimonials ─────────────────────── */

function Testimonials({ c }) {
  const items = c.testimonials || [];
  // Heading rises; quote cards stagger up + scale in as the band enters.
  const sceneRef = useGsapScene((self, q, root) => {
    // Eyebrow only — the heading is a <SplitReveal> and owns its own entrance.
    const eyebrow = q(".hv-tst__head .hv-eyebrow")[0];
    const cards = q(".hv-tst");
    if (eyebrow) {
      gsap.fromTo(eyebrow, { yPercent: 30, autoAlpha: 0 }, {
        yPercent: 0, autoAlpha: 1, ease: "power2.out",
        scrollTrigger: { trigger: root, start: "top 80%", end: "top 50%", scrub: 0.5 },
      });
    }
    if (cards.length) {
      gsap.fromTo(cards, { y: 64, scale: 0.9, autoAlpha: 0 }, {
        y: 0, scale: 1, autoAlpha: 1, ease: "power3.out", stagger: 0.14,
        scrollTrigger: { trigger: root, start: "top 72%", end: "center center", scrub: 0.6 },
      });
    }
  });
  return (
    <section className="hv-section hv-tst-wrap hv-band hv-band--tint" ref={sceneRef}>
      <div className="hv-tst__head">
        <div className="hv-eyebrow">{c.testimonialsEyebrow}</div>
        <SplitReveal as="h2" className="hv-title hv-title--center">{c.testimonialsTitle}</SplitReveal>
      </div>
      <div className="hv-tst__grid">
        {items.map((t, i) => (
          <figure className="hv-tst" key={t.name || i}>
            <span className="hv-tst__mark" aria-hidden="true">&ldquo;</span>
            <blockquote className="hv-tst__quote">{t.quote}</blockquote>
            <figcaption className="hv-tst__by">
              <span className="hv-tst__name">{t.name}</span>
              <span className="hv-tst__role">{t.role}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────────── FAQ ───────────────────────────── */

function Faq({ c }) {
  const items = c.faqs || [];
  const [open, setOpen] = useState(0);

  // Aside slides in from the left; each FAQ row staggers in from the right.
  const sceneRef = useGsapScene((self, q, root) => {
    // Slide the aside's non-heading content only; the <SplitReveal> heading
    // handles itself (fading its container would swallow the masked lines).
    const asideBits = q(".hv-faq__aside .hv-eyebrow, .hv-faq__aside p, .hv-faq__aside a");
    const rows = q(".hv-faq");
    if (asideBits.length) {
      gsap.fromTo(asideBits, { x: -44, autoAlpha: 0 }, {
        x: 0, autoAlpha: 1, ease: "power2.out", stagger: 0.06,
        scrollTrigger: { trigger: root, start: "top 80%", end: "top 50%", scrub: 0.5 },
      });
    }
    if (rows.length) {
      gsap.fromTo(rows, { x: 40, autoAlpha: 0 }, {
        x: 0, autoAlpha: 1, ease: "power2.out", stagger: 0.1,
        scrollTrigger: { trigger: root, start: "top 74%", end: "center 65%", scrub: 0.5 },
      });
    }
  });

  return (
    <section className="hv-section hv-faq-wrap" ref={sceneRef}>
      <div className="hv-faq__grid">
        <div className="hv-faq__aside">
          <div className="hv-eyebrow">{c.faqEyebrow}</div>
          <SplitReveal as="h2" className="hv-title">{c.faqTitle}</SplitReveal>
        </div>

        <div className="hv-faq__list">
          {items.map((f, i) => {
            const isOpen = open === i;
            return (
              <div className={`hv-faq ${isOpen ? "is-open" : ""}`} key={f.q || i}>
                <h3 className="hv-faq__q">
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                  >
                    <span>{f.q}</span>
                    <span className="hv-faq__icon" aria-hidden="true" />
                  </button>
                </h3>
                {/* Height animates via a grid-rows trick so the panel can
                    size to its content without a fixed max-height guess. */}
                <div className="hv-faq__panel" role="region" hidden={!isOpen}>
                  <div className="hv-faq__panel-inner">
                    <p>{f.a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Closing CTA ───────────────────────── */

function ClosingCta({ c, content }) {
  // Zoom-into-scene close: the background image scales up as the section
  // scrolls through (camera pushing in), heading rises + scales to settle.
  const sceneRef = useGsapScene((self, q, root) => {
    const bg = q(".hv-cta__bg")[0];
    const title = q(".hv-cta__title")[0];
    if (bg) {
      gsap.fromTo(bg, { scale: 1.18, yPercent: -4 }, {
        scale: 1, yPercent: 0, ease: "none",
        scrollTrigger: { trigger: root, start: "top bottom", end: "bottom top", scrub: 0.8 },
      });
    }
    if (title) {
      gsap.fromTo(title, { scale: 1.12, yPercent: 30, autoAlpha: 0 }, {
        scale: 1, yPercent: 0, autoAlpha: 1, ease: "power2.out",
        scrollTrigger: { trigger: root, start: "top 75%", end: "top 40%", scrub: 0.5 },
      });
    }
  });

  return (
    <section className="hv-cta-wrap" ref={sceneRef}>
      <div className="hv-cta">
        {c.ctaImage && (
          <img className="hv-cta__bg" src={c.ctaImage} alt="" aria-hidden="true" loading="lazy" />
        )}
        <div className="hv-cta__scrim" aria-hidden="true" />
        <Reveal as="div" className="hv-cta__inner" variant="left">
          <div className="hv-eyebrow hv-eyebrow--on-dark">{c.ctaEyebrow}</div>
          <h2 className="hv-cta__title">{c.ctaTitle}</h2>
          <p className="hv-cta__body">{c.ctaBody}</p>
          <div className="hv-cta__actions">
            <button type="button" className="hv-btn hv-btn--primary" onClick={openLeadModal}>
              {c.ctaPrimary}
            </button>
            <a href={`tel:${content.settings.phoneRaw}`} className="hv-btn hv-btn--ghost-light">
              {c.ctaSecondary}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ───────────────────────────── Page ───────────────────────────── */

export default function HomePage() {
  const content = useContent();
  const c = content.homeV2;

  return (
    <PageShell title={c.metaTitle} description={c.metaDescription} fullBleedHero>
      <Hero c={c} />
      <About c={c} />
      <Stats c={c} />
      <Projects c={c} />

      {/* The real 240-frame scroll-scrub build section, ported from the old
          homepage. standalone = run without the legacy phase system. */}
      <Suspense fallback={<div className="hv-construction-fallback" />}>
        <ConstructionPhase standalone />
      </Suspense>

      <Testimonials c={c} />
      <Faq c={c} />
      <ClosingCta c={c} content={content} />
    </PageShell>
  );
}
